import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';

import { createMongoAbility, type MongoAbility } from '@casl/ability';

import { ROUTES } from '@cloud-ui/constants';
import { useUserTeamAbilities } from '@cloud-ui/hooks/useUser';
import { type Subjects } from '@shared/dto/rbac';

import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { useTeamsContext } from './TeamsContext';

export type Action = 'manage' | 'read' | 'create' | 'update' | 'delete' | 'comment';

export type AppAbility = MongoAbility;

// Type definition matching the shape of permission rules
type PermissionRule = { action: string; subject: string };

interface RbacContextType {
  ability: AppAbility | null | undefined;
  isOrgAdmin: boolean;
  isLoading: boolean;
  isError: boolean;
}

export const RbacContext = createContext<RbacContextType | null>(null);

interface RbacProviderProps {
  children: ReactNode;
}

export const useRbac = () => {
  const context = useContext(RbacContext);
  if (context == null) {
    throw new Error('useRbac must be used within an RbacProvider');
  }
  return context;
};

export const useCan = (action: Action, subject: Subjects) => {
  const { ability } = useRbac();
  return ability?.can(action, subject) || false;
};

export const useTeamPermissions = (teamId?: string) => {
  const { data, isLoading, isError, fetchStatus } = useUserTeamAbilities(teamId);
  const ability = useMemo(
    () => (data ? createMongoAbility(data as unknown as PermissionRule[]) : null),
    [data],
  );
  const can = useCallback(
    (action: Action, subject: Subjects) => ability?.can(action, subject) || false,
    [ability],
  );

  return {
    can,
    isLoading: isLoading && fetchStatus !== 'idle',
    isError,
  };
};

export const RbacProvider = ({ children }: RbacProviderProps) => {
  const { pathname } = useLocation();
  const { currentTeam, isInitializing: teamsInitializing } = useTeamsContext();
  const { data, isLoading, isError, fetchStatus } = useUserTeamAbilities(currentTeam?.id);

  // Server rules already match CASL's { action, subject } shape.
  const ability = data ? createMongoAbility(data as unknown as PermissionRule[]) : null;

  // Wait for team selection before mounting content that needs its initial permissions.
  // When query is disabled (no user/team), isLoading may be true but fetchStatus is 'idle'.
  // We should allow unauthenticated users to access public pages.
  const isActuallyLoading = teamsInitializing || (isLoading && fetchStatus !== 'idle');
  // Session transitions can start permission loading while these public pages retain a
  // one-use login or invitation continuation. Keep them mounted until navigation succeeds.
  const isSessionPage = [ROUTES.login, ROUTES.logout, ROUTES.register, ROUTES.oauthCallback].some(
    (route) => matchPath({ path: route, end: true }, pathname) !== null,
  );
  if (isActuallyLoading && !isSessionPage) {
    return null;
  }

  return (
    <RbacContext.Provider value={{ ability, isOrgAdmin: false, isLoading, isError }}>
      {children}
    </RbacContext.Provider>
  );
};

/**
 * Requires that the user has the given action permission for the given subject; otherwise, redirects to the home page.
 */
export const useRequireElseRedirect = (action: Action, subject: Subjects) => {
  const navigate = useNavigate();
  const canDoAction = useCan(action, subject);

  useEffect(() => {
    if (!canDoAction) {
      navigate(ROUTES.root);
    }
  }, [canDoAction, navigate]);
};
